/**
 * Lookup helpers for site content sourced from custom_objects.site.
 *
 * Each record's content_{lang} field is parsed into named sections by
 * `##` headers, with `###` sub-items. A typical record:
 *
 *   ## heading
 *   Four kinds of traveler.
 *
 *   ## meta
 *   Pick the energy. We staff, plan, drive, and feed the rest.
 *
 *   ## cards
 *   ### Girls' Trips.
 *   ### Bro-cations.
 *
 * Frontend use:
 *
 *   text(site, lang, "homepage_personas", "heading", "Four kinds of traveler.")
 *   items(site, lang, "homepage_personas", "cards")    // [{title, body}, ...]
 *   bullets(site, lang, "homepage_personas", "cards")  // string[]
 *
 * All lookups fall back to defaults — the page renders even when a section
 * isn't authored yet or the build snapshot is empty.
 */

import type {
  ParsedTripContent,
  ParsedTripSection,
  SiteSnapshot,
  TripSectionItem,
} from "../lib/products";

export type Lang = "en" | "es";

function getSection(
  site: SiteSnapshot | undefined,
  lang: Lang,
  slug: string,
  key: string,
): ParsedTripSection | undefined {
  const rec = site?.[slug];
  if (!rec) return undefined;
  // Treat an empty ParsedTripContent (no sections) as missing so the
  // rec[lang] ?? rec.en chain actually fires when only one language is
  // authored.
  const wanted = rec[lang];
  const localized: ParsedTripContent | undefined =
    wanted && wanted.order.length > 0 ? wanted : rec.en;
  return localized?.sections?.[key];
}

/** Image URL for a section. Images aren't localized, so always read from the
 *  EN block. Falls back to `fallback` (a hardcoded default URL) if missing. */
export function image(
  site: SiteSnapshot | undefined,
  slug: string,
  key: string,
  fallback: string,
): string {
  const body = site?.[slug]?.en?.sections?.[key]?.body?.trim();
  return body || fallback;
}

/** Plain text body of a section. Falls back to `fallback` if missing. */
export function text(
  site: SiteSnapshot | undefined,
  lang: Lang,
  slug: string,
  key: string,
  fallback: string,
): string {
  return getSection(site, lang, slug, key)?.body?.trim() || fallback;
}

/** Sub-items (### inside ## key). Falls back to empty array. */
export function items(
  site: SiteSnapshot | undefined,
  lang: Lang,
  slug: string,
  key: string,
): TripSectionItem[] {
  return getSection(site, lang, slug, key)?.items ?? [];
}

/** Bullet list (`- item`) inside the section body. Falls back to empty array. */
export function bullets(
  site: SiteSnapshot | undefined,
  lang: Lang,
  slug: string,
  key: string,
): string[] {
  return getSection(site, lang, slug, key)?.bullets ?? [];
}

/** Convenience: split a section's body on newlines and trim — useful for
 *  multi-line short content like quote attribution. */
export function lines(
  site: SiteSnapshot | undefined,
  lang: Lang,
  slug: string,
  key: string,
): string[] {
  const body = getSection(site, lang, slug, key)?.body;
  if (!body) return [];
  return body
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
}
